import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for auto-saving form data with debouncing
 * @param {Function} saveFn - Async function to save data
 * @param {number} delay - Debounce delay in milliseconds (default: 1000ms)
 */
export function useAutoSave(saveFn, delay = 1000) {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState(null);
  const timeoutRef = useRef(null);
  const pendingDataRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Save function that handles debouncing — merges multiple rapid calls
  const save = useCallback(async (data) => {
    // Merge with any existing pending data so rapid sequential calls don't overwrite each other
    pendingDataRef.current = { ...(pendingDataRef.current || {}), ...data };
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(async () => {
      const payload = pendingDataRef.current;
      pendingDataRef.current = null; // Reset for next batch
      try {
        setIsSaving(true);
        setError(null);
        
        const result = await saveFn(payload);
        
        setLastSaved(new Date());
        setIsSaving(false);
        
        return result;
      } catch (err) {
        setError(err.message || 'Failed to save');
        setIsSaving(false);
        throw err;
      }
    }, delay);
  }, [saveFn, delay]);

  // Immediate save (bypass debounce) — also flushes any pending merged data
  const saveNow = useCallback(async (data) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Merge with any unflushed pending data
    const payload = { ...(pendingDataRef.current || {}), ...data };
    pendingDataRef.current = null;
    
    try {
      setIsSaving(true);
      setError(null);
      
      const result = await saveFn(payload);
      
      setLastSaved(new Date());
      setIsSaving(false);
      
      return result;
    } catch (err) {
      setError(err.message || 'Failed to save');
      setIsSaving(false);
      throw err;
    }
  }, [saveFn]);

  return {
    save,
    saveNow,
    isSaving,
    lastSaved,
    error
  };
}

export default useAutoSave;
